/**
 * Le clavier réduit à un témoin, sur un téléphone.
 *
 * # Pourquoi la vue peut en décider seule
 *
 * Elle ne sait rien d'un replay ni d'un récital, et n'a pas à le savoir : sur
 * un téléphone, **le studio n'est visible que pour quelqu'un qui regarde**.
 * Tout le reste est recouvert par `HandheldNotice`, qui ne s'efface que devant
 * un replay — jamais devant le joueur, jamais devant l'interprète, à qui il dit
 * au contraire qu'il leur faut un instrument, et pas davantage devant le
 * spectateur d'un récital en direct, à qui il conseille encore de tourner
 * l'appareil. La conclusion « personne ne va poser les doigts sur ces touches »
 * se tire donc du seul média, sans qu'un état ait à descendre jusqu'ici.
 *
 * Si ce calque venait à laisser passer un joueur, c'est ici qu'il faudrait
 * revenir : la règle ci-dessous perdrait sa justification le jour même.
 *
 * # Ce que le témoin change, et pourquoi les deux vont ensemble
 *
 * **La hauteur.** Une bande de 110 px sur les 306 px de canvas d'un iPhone
 * couché, c'est 36 % de la surface donnée à un instrument que personne ne
 * touche, pris sur le rouleau qu'on est venu regarder. Le témoin la ramène à
 * {@link WITNESS_KEYBOARD_HEIGHT} : les touches restent des touches, elles
 * s'allument toujours quand une note sonne — c'est tout ce qu'on leur demande
 * ici —, et le rouleau récupère une soixantaine de pixels, un tiers de sa
 * hauteur.
 *
 * **La largeur.** Le plancher tactile de 40 px par touche blanche
 * (`MIN_WHITE_KEY_WIDTH_COARSE`) existe pour qu'un doigt puisse viser une
 * touche. Sans doigt à loger, il ne protège plus rien et coûte cher. Mesuré le
 * 2026-09-19 sur le récital d'essai, téléphone debout : canvas de 346 px,
 * touches bloquées à 40 px, soit **8,6 touches blanches à l'écran** pour une
 * tessiture jouée de 20 (Ré3–Si5). On regardait une octave d'un morceau qui en
 * fait presque trois, et le rouleau passait son temps à défiler derrière les
 * notes. Le témoin descend donc à {@link WITNESS_MIN_WHITE_KEY_WIDTH}.
 *
 * Les deux ne se séparent pas : couper la hauteur en gardant des touches de
 * 40 px donnerait une bande écrasée qui ne ressemble plus à un clavier, et
 * montrerait toujours aussi peu de musique.
 *
 * # Le plafond, lui, ne bouge pas
 *
 * Seul le **plancher** descend ; le plafond reste celui du pointeur fin. C'est
 * ce qui garantit que le paysage ne bouge pas d'un pixel : sur ses 780 px de
 * canvas, la largeur voulue (35 px) tapait déjà dans le plafond (30 px), et un
 * plancher plus bas ne change rien à une borne haute qui mord.
 */

/**
 * Hauteur de la bande de touches en témoin, en px.
 *
 * 48 px sur les 306 px de canvas d'un iPhone couché, soit un sixième au lieu
 * d'un tiers. Le dessin des touches est entièrement proportionnel (la noire
 * vaut 60 % de la bande, l'ombre basse 16 %), donc la silhouette tient : on
 * reconnaît un clavier, et une touche allumée se voit d'un mètre. En dessous
 * de ~40 px l'ombre basse, qui a un plancher de 10 px, prend le quart de la
 * touche et la bande se lit comme une frise.
 *
 * Debout, ce n'est même plus un rognage : à 15 px de touche, la proportion
 * historique (`KEYBOARD_HEIGHT_PER_WHITE_KEY`, 110/30) réclame 55 px. Les 48
 * qu'on impose sont à peu près ce que le clavier demanderait de lui-même.
 */
export const WITNESS_KEYBOARD_HEIGHT = 48;

/**
 * Plancher de largeur de touche blanche en témoin, en px.
 *
 * Ce n'est pas un nombre choisi ici : c'est `FINGER_MIN_WIDTH`
 * (`lib/engine/components/piano-roll/notes.ts`), la largeur au-dessous de
 * laquelle le rouleau cesse de poser un doigté sur une note, faute de place
 * pour le dessiner. Le produit avait donc déjà répondu à la question « à partir
 * de quelle finesse une note ne porte plus rien » ; le témoin reprend sa
 * réponse au lieu d'en inventer une seconde.
 *
 * Ce qu'il donne, sur le canvas de 346 px d'un téléphone debout : la borne ne
 * mord qu'au-delà de 24 touches blanches de tessiture. Le récital d'essai en
 * demande 22, il tient donc **en entier** à l'écran — on passe du régime
 * « défiler derrière les notes » au régime « le morceau est cadré ». La
 * tessiture médiane du corpus vaut 27 touches : elle en montre 24 sur 27, et
 * l'auto-follow fait le reste.
 *
 * Les deux constantes ne peuvent pas être importées l'une depuis l'autre
 * (couches différentes) ; si celle du rouleau bouge, celle-ci doit suivre.
 */
export const WITNESS_MIN_WHITE_KEY_WIDTH = 14;

/**
 * Média « téléphone », mémorisé une fois.
 *
 * Même montage — et mêmes raisons — que `isCoarsePointer` dans
 * `piano-component.ts` : un `MediaQueryList` est vivant, donc relire `.matches`
 * à chaque layout ne coûte rien et suit la rotation sans abonnement. La
 * création est paresseuse parce que ce module est atteint depuis du code rendu
 * côté serveur, où `window` n'existe pas.
 */
let handheldMedia: readonly MediaQueryList[] | null | undefined;

/**
 * Les copies, à l'identique, de `HANDHELD_QUERIES` côté hooks — et, en CSS, de
 * la variante `handheld:` de `app/globals.css`. Exportées pour qu'un test tienne
 * les trois ensemble : rien d'autre ne les y oblige, et deux seuils qui
 * divergent donneraient un clavier témoin sous un écran qui a gardé sa barre
 * latérale. Elles ne peuvent pas être importées d'ici, cette couche étant sans
 * React.
 */
export const WITNESS_KEYBOARD_QUERIES: readonly string[] = [
  "(pointer: coarse) and (max-height: 480px)",
  "(pointer: coarse) and (orientation: portrait) and (not (min-width: 48rem))",
];

/** Vrai sur un téléphone, où le clavier n'est plus qu'un témoin. */
export function isWitnessKeyboard(): boolean {
  if (handheldMedia === undefined) {
    handheldMedia =
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? WITNESS_KEYBOARD_QUERIES.map((query) => window.matchMedia(query))
        : null;
  }

  return handheldMedia?.some((media) => media.matches) ?? false;
}
